import { useEffect, useRef, useState } from "react";
import { useIsLgScreenUp } from "src/utils/windowUtils"; // Import the hook
import styled from "styled-components";
import { FaVolumeHigh, FaVolumeXmark } from "react-icons/fa6";

const VideoComponent = ({
  desktop,
  mobile,
  soundControl = true,
  className,
  videoProps = {},
  containerStyle = {},
  audioTogglePosition = { bottom: "10px", right: "10px" },
  externalMuted = null, // Allow parent to control mute state
  onMuteChange = null // Callback when mute state changes
}) => {
  const videoRef = useRef(null);
  const isLgScreenUp = useIsLgScreenUp(); // Use the hook to determine if screen width is > 991
  const [internalMuted, setInternalMuted] = useState(true); // Internal state to manage mute/unmute

  // Use external muted state if provided, otherwise use internal state
  // If no sound control and no external control, always stay muted
  const isMuted = externalMuted !== null ? externalMuted : (!soundControl ? true : internalMuted);

  useEffect(() => {
    const video = videoRef.current;
    let observer;

    const updateVideoSrc = () => {
      if (video) {
        video.src = isLgScreenUp ? desktop : mobile;
        video.load();
        video.muted = true; // Always start muted for autoplay
      }
    };

    const handleIntersection = (entries) => {
      if (entries[0].isIntersecting) {
        updateVideoSrc(); // Load the appropriate video source based on screen size
        observer.disconnect(); // Stop observing once the video starts loading
      }
    };

    if (video) {
      observer = new IntersectionObserver(handleIntersection, {
        rootMargin: "0px",
        threshold: 0.25, // Start loading when 25% of the video is visible
      });

      observer.observe(video);
    }

    return () => {
      if (observer && video) {
        observer.unobserve(video);
      }
    };
  }, [isLgScreenUp, desktop, mobile]);

  // Update video muted state when external or internal state changes
  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = isMuted;
    }
  }, [isMuted]);

  const toggleMute = () => {
    const video = videoRef.current;
    if (video) {
      const newMutedState = !video.muted;
      video.muted = newMutedState;

      // Update internal state if not externally controlled
      if (externalMuted === null) {
        setInternalMuted(newMutedState);
      }

      // Notify parent of mute state change
      if (onMuteChange) {
        onMuteChange(newMutedState);
      }
    }
  };

  return (
    <VideoHolder className={className} style={containerStyle}>
      <video
        ref={videoRef}
        autoPlay
        loop
        muted={isMuted}
        playsInline
        preload="none" // Use "none" to lazy load the video
        onLoadedMetadata={(e) => {
          e.target.muted = isMuted;
        }}
        {...videoProps}
      >
        <source type="video/mp4" />
      </video>
      {soundControl && (
        <SoundToggleButton onClick={toggleMute} $position={audioTogglePosition}>
          {isMuted ? <FaVolumeXmark size={24} /> : <FaVolumeHigh size={24} />}
        </SoundToggleButton>
      )}
    </VideoHolder>
  );
};

const VideoHolder = styled.div`
  width: 100%;
  position: relative;
  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
  }
`;

// Styled component for the sound toggle button
const SoundToggleButton = styled.button`
  position: absolute;
  bottom: ${props => props.$position?.bottom || "10px"};
  right: ${props => props.$position?.right || "10px"};
  background: rgba(0, 0, 0, 0.5);
  border: none;
  border-radius: 50%;
  padding: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  svg {
    color: white;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.7);
  }
`;

export default VideoComponent;
